import { useState, useMemo, useEffect } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, fmtMXN, WHATSAPP_NUMBER } from "@/lib/api";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { Calendar as CalendarIcon, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { PROPERTY_LIST } from "@/lib/properties";

const toISO = (d) => format(d, "yyyy-MM-dd");

export default function Booking({ property, allowPicker = false, quizUnlock = null, defaultKit = false, scrollAnchor = "reservar" }) {
  const { t, lang } = useLang();
  const dateLocale = lang === "es" ? es : enUS;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // If no quizUnlock provided, check for a creatures bonus token in localStorage (3-min flash)
  const effectiveQuizUnlock = (() => {
    if (quizUnlock) return quizUnlock;
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem("vpv_creatures_bonus_v1") : null;
      if (!raw) return null;
      const b = JSON.parse(raw);
      if (b?.expiresAt && new Date(b.expiresAt).getTime() > Date.now()) {
        return { token: b.token, expiresAt: b.expiresAt };
      }
    } catch {}
    return null;
  })();

  // If no property prop and picker mode is allowed, use the first available
  const [pickedId, setPickedId] = useState(property?.id || PROPERTY_LIST[0].id);
  const activeProperty = property || PROPERTY_LIST.find((p) => p.id === pickedId) || PROPERTY_LIST[0];
  const showPicker = allowPicker && !property;

  const [range, setRange] = useState();
  const [guests, setGuests] = useState("2");
  const [quote, setQuote] = useState(null);
  const [step, setStep] = useState(1);
  const [blockedRanges, setBlockedRanges] = useState([]);

  // Load Airbnb iCal + paid-booking blocked ranges so the calendar can grey them out
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/properties/${activeProperty.id}/blocked-dates`);
        if (!cancelled) setBlockedRanges(data?.ranges || []);
      } catch {
        if (!cancelled) setBlockedRanges([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeProperty.id]);

  // Convert blocked ranges into react-day-picker DateInterval objects.
  // Use DateInterval ({ after, before }) instead of DateRange to avoid edge cases
  // with empty arrays in modifiers prop.
  const bookedRanges = useMemo(() => {
    try {
      return blockedRanges
        .map((r) => {
          const s = new Date(r.start + "T00:00:00");
          const e = new Date(r.end + "T00:00:00");
          const last = new Date(e); last.setDate(last.getDate() - 1);
          if (isNaN(s.getTime()) || isNaN(last.getTime()) || s > last) return null;
          return { from: s, to: last };
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  }, [blockedRanges]);

  const disabledMatcher = useMemo(() => {
    return [{ before: today }, ...bookedRanges];
  }, [bookedRanges, today]);

  // Visual modifiers: differentiate "past" vs "booked" so user understands
  // why dates look greyed. Past = strikethrough light grey. Booked = red strikethrough.
  const isBookedDay = useMemo(() => {
    return (day) => {
      const dayTime = new Date(day); dayTime.setHours(0, 0, 0, 0);
      for (const r of blockedRanges) {
        const blockStart = new Date(r.start + "T00:00:00");
        const blockEnd = new Date(r.end + "T00:00:00");
        // Block covers nights [blockStart, blockEnd)
        if (dayTime >= blockStart && dayTime < blockEnd) return true;
      }
      return false;
    };
  }, [blockedRanges]);

  const dayModifiers = useMemo(() => ({
    past: { before: today },
    booked: isBookedDay,
  }), [isBookedDay, today]);

  const dayModifiersClassNames = {
    past: "vpv-day-past",
    booked: "vpv-day-booked",
  };

  // Returns true if [from, to) contains any disabled day (excluding the check-out day itself,
  // which is free). Defensive validator since react-day-picker `excludeDisabled` doesn't
  // always reset multi-month range selections.
  const rangeHasBlocked = (from, to) => {
    if (!from || !to) return false;
    const fromTime = new Date(from); fromTime.setHours(0, 0, 0, 0);
    const toTime = new Date(to); toTime.setHours(0, 0, 0, 0);
    for (const r of blockedRanges) {
      const blockStart = new Date(r.start + "T00:00:00");
      const blockEnd = new Date(r.end + "T00:00:00");
      // Block covers nights [blockStart, blockEnd). Overlap with selection
      // [fromTime, toTime) iff blockStart < toTime AND blockEnd > fromTime.
      if (blockStart < toTime && blockEnd > fromTime) return true;
    }
    return false;
  };

  // Notify the global creatures component to hide once the user steps into the booking form
  useEffect(() => {
    try { window.dispatchEvent(new CustomEvent("vpv:booking-active", { detail: { active: step >= 2 } })); } catch {}
    return () => {
      try { window.dispatchEvent(new CustomEvent("vpv:booking-active", { detail: { active: false } })); } catch {}
    };
  }, [step]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [promoCode, setPromoCode] = useState("");
  const [includeKit] = useState(false);
  const [loading, setLoading] = useState(false);

  // Quiz countdown: ticks every second while quizUnlock is provided
  const [now, setNowState] = useState(Date.now());
  useEffect(() => {
    if (!effectiveQuizUnlock?.expiresAt) return;
    const id = setInterval(() => setNowState(Date.now()), 1000);
    return () => clearInterval(id);
  }, [effectiveQuizUnlock?.expiresAt]);
  const quizSecondsLeft = effectiveQuizUnlock?.expiresAt ? Math.max(0, Math.floor((new Date(effectiveQuizUnlock.expiresAt).getTime() - now) / 1000)) : 0;
  const quizActive = !!effectiveQuizUnlock?.token && quizSecondsLeft > 0;
  const fmtCountdown = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return Math.round((range.to - range.from) / (1000 * 60 * 60 * 24));
  }, [range]);

  const fetchQuote = async () => {
    if (!range?.from || !range?.to) {
      toast.error(t("booking.select_dates_first"));
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.post("/quote", {
        property_id: activeProperty.id,
        check_in: toISO(range.from),
        check_out: toISO(range.to),
        guests: parseInt(guests, 10),
        promo_code: promoCode.trim() || null,
        quiz_unlock_token: quizActive ? effectiveQuizUnlock.token : null,
        include_kit: includeKit,
      });
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("booking.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  // Re-quote when kit toggled if we already have a quote
  useEffect(() => {
    if (!quote) return;
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeKit]);

  const goToStep2 = async () => {
    if (!range?.from || !range?.to) {
      toast.error(t("booking.select_dates_first"));
      return;
    }
    // Guard rail: reject if any night inside the selected range is blocked
    if (rangeHasBlocked(range.from, range.to)) {
      toast.error(lang === "es"
        ? "Hay fechas reservadas dentro del rango seleccionado. Elige otras fechas."
        : "There are booked dates inside the selected range. Pick other dates.");
      return;
    }
    // Server-side check just in case the iCal updated after the user picked dates
    try {
      const ci = range.from.toISOString().slice(0, 10);
      const co = range.to.toISOString().slice(0, 10);
      const { data } = await api.get(`/availability?property_id=${activeProperty.id}&check_in=${ci}&check_out=${co}`);
      if (data?.available === false) {
        toast.error(lang === "es"
          ? "Esas fechas ya no están disponibles. Recarga el calendario."
          : "Those dates are no longer available. Refresh the calendar.");
        return;
      }
    } catch {
      // Soft-fail: if availability check fails, server-side /api/bookings will still reject.
    }
    if (!quote) await fetchQuote();
    setStep(2);
  };

  const validateForm = () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(t("booking.fill_all"));
      return false;
    }
    return true;
  };

  const createBooking = async () => {
    const { data } = await api.post("/bookings", {
      property_id: activeProperty.id,
      check_in: toISO(range.from),
      check_out: toISO(range.to),
      guests: parseInt(guests, 10),
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: form.message.trim(),
      promo_code: promoCode.trim() || null,
      quiz_unlock_token: quizActive ? effectiveQuizUnlock.token : null,
      include_kit: includeKit,
    });
    return data;
  };

  const handlePayStripe = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const booking = await createBooking();
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        booking_id: booking.booking_id,
        origin_url: origin,
      });
      window.location.href = data.url;
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 409 ? t("booking.dates_taken") : (e?.response?.data?.detail || t("booking.error_generic"));
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleWhatsapp = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        property_id: activeProperty.id,
        message: `Reserva ${activeProperty.name} — ${toISO(range.from)} a ${toISO(range.to)} · ${guests} huéspedes · Total directo ${fmtMXN(quote?.total_web)}. ${form.message}`,
      });
      const text = encodeURIComponent(
        `Hola, me interesa reservar ${activeProperty.name} en Villas Pura Vida.\n\n` +
          `📅 ${toISO(range.from)} → ${toISO(range.to)} (${quote?.nights} ${t("booking.nights")})\n` +
          `👥 ${guests} ${t("booking.guests").toLowerCase()}\n` +
          `💰 ${t("booking.web_total")}: ${fmtMXN(quote?.total_web)} (${t("booking.you_save")} ${fmtMXN(quote?.savings)})\n\n` +
          `Nombre: ${form.name}\nCorreo: ${form.email}\nTel: ${form.phone}\n\n${form.message}`,
      );
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
      toast.success(t("booking.success_inquiry"));
    } catch {
      toast.error(t("booking.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const dateLabel = (d) => (d ? format(d, "dd MMM yyyy", { locale: dateLocale }) : t("booking.pick_dates"));

  return (
    <section id="reservar" data-testid="booking-section" className="bg-[#FAFAF9] py-24 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="text-eyebrow text-[#E07A5F] mb-4">{t("booking.eyebrow")} — {activeProperty.short_name}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900 mb-6">
              {t("booking.title")}
            </h2>
            <p className="text-stone-700 text-lg leading-relaxed mb-8">{t("booking.sub")}</p>

            <div className="flex flex-col gap-4">
              <div className="inline-flex items-center gap-3 text-sm text-stone-600">
                <ShieldCheck className="w-5 h-5 text-[#81B29A]" />
                Pago seguro con Stripe
              </div>
              <div className="inline-flex items-center gap-3 text-sm text-stone-600">
                <i className="fa-solid fa-tag text-[#E07A5F]" />
                10% menos que el precio en Airbnb
              </div>
              <div className="inline-flex items-center gap-3 text-sm text-stone-600">
                <i className="fa-brands fa-whatsapp text-[#81B29A]" />
                ¿Prefieres WhatsApp? También puedes solicitar por ahí.
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div data-testid="booking-card" className="bg-white border border-stone-200 p-6 md:p-10 shadow-sm">
              {quizActive && (
                <div data-testid="quiz-offer-banner" className="mb-6 -mt-2 -mx-2 md:-mx-6 relative overflow-hidden">
                  <style>{`
                    @keyframes quizPulseRed {
                      0%, 100% { box-shadow: inset 0 0 0 2px rgba(255,255,255,0.0), 0 0 0 0 rgba(220, 38, 38, 0.6); }
                      50% { box-shadow: inset 0 0 0 2px rgba(255,255,255,0.0), 0 0 0 14px rgba(220, 38, 38, 0); }
                    }
                    @keyframes quizCountdownTick {
                      0%, 100% { transform: scale(1); }
                      50% { transform: scale(1.08); }
                    }
                    @keyframes quizBgShimmer {
                      0% { background-position: 0% 50%; }
                      100% { background-position: 200% 50%; }
                    }
                  `}</style>
                  <div
                    className="px-4 md:px-6 py-4 text-white flex items-center justify-between gap-4 flex-wrap relative"
                    style={{
                      backgroundImage: "linear-gradient(110deg, #DC2626 0%, #B91C1C 35%, #DC2626 50%, #EF4444 65%, #DC2626 100%)",
                      backgroundSize: "200% 100%",
                      animation: "quizPulseRed 2s ease-in-out infinite, quizBgShimmer 4s linear infinite",
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl md:text-3xl leading-none" style={{ animation: "quizCountdownTick 1s ease-in-out infinite" }}>🔥</span>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] opacity-95 mb-0.5 font-bold">¡Oferta exclusiva quiz!</p>
                        <p className="font-serif-display text-xl md:text-2xl leading-tight font-semibold">10% off · sólo ahora</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-[0.2em] opacity-90 font-bold">Expira en</p>
                      <p
                        className="font-mono text-3xl md:text-4xl font-black tabular-nums tracking-tight drop-shadow-md"
                        style={{ animation: "quizCountdownTick 1s ease-in-out infinite" }}
                      >
                        {fmtCountdown(quizSecondsLeft)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {step === 1 && (
                <div className="space-y-6">
                  {showPicker && (
                    <div>
                      <Label className="text-eyebrow text-stone-500">Propiedad</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {PROPERTY_LIST.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => {
                              if (p.id === pickedId) return;
                              setPickedId(p.id);
                              // Reset booking state so user picks fresh for the new property
                              setRange(undefined);
                              setQuote(null);
                              setBlockedRanges([]);
                              // Clamp guests to capacity
                              if (parseInt(guests, 10) > p.capacity) {
                                setGuests(String(p.capacity));
                              }
                            }}
                            data-testid={`picker-${p.id}`}
                            className={`text-left px-4 py-3 border transition-colors ${
                              pickedId === p.id
                                ? "bg-stone-900 text-stone-50 border-stone-900"
                                : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                            }`}
                          >
                            <div className="font-serif-display text-lg leading-tight">{p.short_name}</div>
                            <div className="text-[10px] uppercase tracking-wider opacity-80 mt-1">
                              Hasta {p.capacity} huéspedes · desde {fmtMXN(p.pricing.web_regular)}/noche
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-eyebrow text-stone-500">{t("booking.check_in")}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            data-testid="checkin-trigger"
                            className="mt-2 w-full border border-stone-300 px-4 py-3 text-left flex items-center justify-between hover:border-stone-500 transition-colors rounded-none"
                          >
                            <span className={range?.from ? "text-stone-900" : "text-stone-400"}>
                              {dateLabel(range?.from)}
                            </span>
                            <CalendarIcon className="w-4 h-4 text-stone-500" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="p-0 w-auto rounded-none">
                          <Calendar
                            mode="range"
                            numberOfMonths={2}
                            selected={range}
                            onSelect={(r) => {
                              // Range crosses a blocked night: restart selection
                              // with the latest click as the new "from" (like excludeDisabled).
                              // Setting to undefined would make the calendar feel dead/disabled.
                              if (r?.from && r?.to && rangeHasBlocked(r.from, r.to)) {
                                toast.error(lang === "es"
                                  ? "Esas fechas cruzan una reservación. Elige otras."
                                  : "Those dates cross a booking. Pick others.");
                                setRange({ from: r.to, to: undefined });
                                setQuote(null);
                                return;
                              }
                              setRange(r);
                              setQuote(null);
                            }}
                            disabled={disabledMatcher}
                            modifiers={dayModifiers}
                            modifiersClassNames={dayModifiersClassNames}
                            locale={dateLocale}
                          />
                          <style>{`
                            .vpv-day-past { text-decoration: line-through; color: #d6d3d1 !important; opacity: 0.7 !important; }
                            .vpv-day-booked { text-decoration: line-through; color: #b91c1c !important; background-color: #fef2f2 !important; opacity: 0.9 !important; font-weight: 500 !important; }
                          `}</style>
                          <div className="px-4 pb-3 pt-2 border-t border-stone-100 space-y-1">
                            <div className="flex items-center gap-2 text-[11px] text-stone-500">
                              <span className="inline-flex items-center justify-center w-5 h-5 text-stone-400 font-normal" style={{ textDecoration: "line-through" }}>15</span>
                              <span>{lang === "es" ? "Fechas pasadas" : "Past dates"}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-stone-500">
                              <span className="inline-flex items-center justify-center w-5 h-5 font-medium" style={{ textDecoration: "line-through", color: "#b91c1c", backgroundColor: "#fef2f2" }}>15</span>
                              <span>{lang === "es" ? "Fechas reservadas" : "Booked dates"}</span>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-eyebrow text-stone-500">{t("booking.check_out")}</Label>
                      <div
                        className="mt-2 w-full border border-stone-300 px-4 py-3 flex items-center justify-between rounded-none bg-stone-50"
                        data-testid="checkout-display"
                      >
                        <span className={range?.to ? "text-stone-900" : "text-stone-400"}>
                          {dateLabel(range?.to)}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-stone-500" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-eyebrow text-stone-500">{t("booking.guests")}</Label>
                    <Select value={guests} onValueChange={(v) => { setGuests(v); setQuote(null); }}>
                      <SelectTrigger data-testid="guests-select" className="mt-2 rounded-none border-stone-300 px-4 py-6 text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: activeProperty.capacity }).map((_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)} data-testid={`guests-option-${i + 1}`}>
                            {i + 1} {i === 0 ? "huésped" : "huéspedes"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {nights > 0 && (
                    <div className="border-t border-stone-200 pt-6">
                      {!quote && (
                        <Button
                          onClick={fetchQuote}
                          disabled={loading}
                          data-testid="get-quote-btn"
                          className="w-full bg-stone-900 hover:bg-stone-700 text-white rounded-none py-6 text-sm uppercase tracking-[0.18em] font-semibold"
                        >
                          {loading ? t("booking.processing") : t("booking.get_quote")}
                        </Button>
                      )}

                      {quote && (
                        <div data-testid="quote-display" className="space-y-3">
                          <div className="flex justify-between text-stone-600">
                            <span>
                              {quote.nights} {t("booking.nights")} · {quote.guests} {t("booking.guests").toLowerCase()}
                            </span>
                            {quote.has_high_season && (
                              <span className="text-xs uppercase tracking-wider text-[#E07A5F]">Temporada alta</span>
                            )}
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="text-stone-500 line-through">{t("booking.airbnb_total")}</span>
                            <span className="text-stone-500 line-through" data-testid="airbnb-total">{fmtMXN(quote.total_airbnb)}</span>
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="font-serif-display text-lg text-stone-700">Subtotal directo</span>
                            <span className="font-serif-display text-xl text-stone-900" data-testid="subtotal-web">{fmtMXN(quote.subtotal_web)}</span>
                          </div>
                          <div className="flex justify-between text-sm bg-[#81B29A]/15 border border-[#81B29A]/40 px-4 py-3">
                            <span className="text-[#3F6A53] font-medium">{t("booking.you_save")}</span>
                            <span className="text-[#3F6A53] font-semibold" data-testid="savings">
                              {fmtMXN(quote.savings)} ({quote.discount_percent}%)
                            </span>
                          </div>
                          {quote.promotion && (
                            <div className="flex justify-between text-sm bg-[#F2CC8F]/30 border border-[#F2CC8F] px-4 py-3" data-testid="promo-applied">
                              <span className="text-stone-800 font-medium">🎉 {quote.promotion.label}{quote.promotion.code ? ` (${quote.promotion.code})` : ""}</span>
                              <span className="text-stone-900 font-semibold">−{fmtMXN(quote.promotion.discount)}</span>
                            </div>
                          )}

                          {/* Final total */}
                          <div className="flex justify-between items-baseline pt-3 border-t border-stone-200">
                            <span className="font-serif-display text-xl">{t("booking.web_total")}</span>
                            <span className="font-serif-display text-3xl text-[#E07A5F]" data-testid="web-total">{fmtMXN(quote.total_web)}</span>
                          </div>

                          {quote.deposit > 0 && (
                            <p className="text-xs text-stone-500 leading-relaxed" data-testid="deposit-note">
                              + Depósito reembolsable de {fmtMXN(quote.deposit)} (se cobra aparte al check-in y se devuelve máx. 24 hrs después de tu salida si todo está en orden).
                            </p>
                          )}
                          <Button
                            onClick={goToStep2}
                            data-testid="continue-btn"
                            className="w-full bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none py-6 text-sm uppercase tracking-[0.18em] font-semibold mt-4"
                          >
                            {t("booking.continue")} <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <button
                    onClick={() => setStep(1)}
                    data-testid="back-btn"
                    className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#E07A5F] transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> {t("booking.back")}
                  </button>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-eyebrow text-stone-500">{t("booking.name")}</Label>
                      <Input id="name" data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none border-stone-300 px-4 py-6 text-base" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-eyebrow text-stone-500">{t("booking.email")}</Label>
                      <Input id="email" type="email" data-testid="form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-none border-stone-300 px-4 py-6 text-base" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-eyebrow text-stone-500">{t("booking.phone")}</Label>
                    <Input id="phone" data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-none border-stone-300 px-4 py-6 text-base" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-eyebrow text-stone-500">{t("booking.message")}</Label>
                    <Textarea id="message" data-testid="form-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-none border-stone-300 px-4 text-base min-h-[80px]" />
                  </div>

                  <div className="border-t border-stone-200 pt-5 space-y-2">
                    <div className="flex justify-between text-sm text-stone-500">
                      <span>{quote?.nights} {t("booking.nights")} · {guests} {t("booking.guests").toLowerCase()}</span>
                      <span className="line-through">{fmtMXN(quote?.total_airbnb)}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="font-serif-display text-xl">{t("booking.web_total")}</span>
                      <span className="font-serif-display text-3xl text-[#E07A5F]">{fmtMXN(quote?.total_web)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button onClick={handlePayStripe} disabled={loading} data-testid="pay-stripe-btn" className="flex-1 bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none py-6 text-sm uppercase tracking-[0.18em] font-semibold">
                      {loading ? t("booking.processing") : t("booking.pay_online")}
                    </Button>
                    <Button onClick={handleWhatsapp} disabled={loading} variant="outline" data-testid="whatsapp-btn" className="flex-1 border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white rounded-none py-6 text-sm uppercase tracking-[0.18em] font-semibold">
                      <i className="fa-brands fa-whatsapp mr-2 text-lg" />
                      {t("booking.whatsapp")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
